import React, { useCallback, useEffect, useMemo, useState } from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { transferMatchingService } from '../../services/transferMatchingService';
import TransferMatchActions from './TransferMatchActions';

const { FiBell, FiChevronDown, FiArrowRight } = FiIcons;

const formatAmount = (amount) => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2
}).format(Number(amount) || 0);

export default function TransferMatchNotification({
  accountId,
  refreshKey,
  onUpdated
}) {
  const [matches, setMatches] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedMatchId, setExpandedMatchId] = useState(null);

  const loadMatches = useCallback(async () => {
    if (!accountId) return;

    setIsLoading(true);

    try {
      const detectedMatches = await transferMatchingService.detectMatches(accountId);
      setMatches(detectedMatches || []);
    } catch (error) {
      console.error('Unable to load transfer match notifications:', error);
      setMatches([]);
    } finally {
      setIsLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    loadMatches();
  }, [loadMatches, refreshKey]);

  const pendingMatches = useMemo(
    () => matches.filter((match) => match.status === 'MATCH_FOUND'),
    [matches]
  );

  const handleUpdated = async () => {
    await loadMatches();
    onUpdated?.();
  };

  if (isLoading || pendingMatches.length === 0) return null;

  return (
    <section className="rounded-xl border border-amber-200 bg-amber-50/80 p-4 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
            <SafeIcon icon={FiBell} />
          </span>
          <div>
            <h2 className="text-[13px] font-semibold text-amber-950">
              Transfer match found
            </h2>
            <p className="mt-1 text-[11px] text-amber-800">
              We found {pendingMatches.length} possible bank and credit-card transfer
              {pendingMatches.length === 1 ? '' : 's'}. Confirm if these transactions
              represent the same transfer, or ignore the suggestion.
            </p>
          </div>
        </div>

        <span className="inline-flex w-fit rounded-full bg-amber-100 px-2.5 py-1 text-[10px] font-semibold text-amber-800">
          Needs your decision
        </span>
      </div>

      <div className="mt-3 space-y-2">
        {pendingMatches.map((match) => {
          const isExpanded = expandedMatchId === match.id;

          return (
            <div
              key={match.id}
              className="rounded-lg border border-amber-200 bg-white px-3 py-2.5"
            >
              <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
                <button
                  type="button"
                  onClick={() => setExpandedMatchId(isExpanded ? null : match.id)}
                  className="flex min-w-0 items-center gap-2 text-left"
                >
                  <SafeIcon
                    icon={FiChevronDown}
                    className={`shrink-0 text-amber-600 transition-transform ${
                      isExpanded ? 'rotate-180' : ''
                    }`}
                  />
                  <span className="truncate text-[12px] font-medium text-slate-800">
                    {formatAmount(match.amount)} transfer
                  </span>
                  <SafeIcon icon={FiArrowRight} className="shrink-0 text-slate-400" />
                  <span className="text-[11px] text-slate-500">
                    {match.outgoing_date} → {match.incoming_date}
                  </span>
                </button>

                <TransferMatchActions
                  match={match}
                  onUpdated={handleUpdated}
                  onReview={() => setExpandedMatchId(isExpanded ? null : match.id)}
                />
              </div>

              {isExpanded && (
                <div className="mt-2 grid grid-cols-1 gap-2 border-t border-slate-100 pt-2 text-[11px] text-slate-600 sm:grid-cols-2">
                  <div>
                    <span className="font-medium text-slate-800">Money out</span>
                    <p>{match.outgoing_date}</p>
                    <p className="break-all text-[10px] text-slate-400">
                      Transaction {match.outgoing_transaction_id}
                    </p>
                  </div>
                  <div>
                    <span className="font-medium text-slate-800">Money in</span>
                    <p>{match.incoming_date}</p>
                    <p className="break-all text-[10px] text-slate-400">
                      Transaction {match.incoming_transaction_id}
                    </p>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}